/**
 * Popover component with viewport-aware positioning and mobile modal mode.
 *
 * Features:
 * - Flip/shift positioning to stay within viewport (via Floating UI)
 * - Auto-update on scroll/resize
 * - Mobile: switches to centered modal-like presentation
 * - Max-height with internal scrolling (qb-scroll)
 * - Focus management (ESC to close, focus trap in modal mode)
 *
 * Root cause note: The original implementation used naive clamping which
 * could still place popovers at viewport edges without flipping. This
 * rewrite uses Floating UI for proper collision detection and flip/shift.
 */

import type { ReactNode } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { useIsMobile } from "../../hooks/useMediaQuery";
import type { PopoverSide, PopoverAlign } from "./popoverPositioning";
import { useAnchoredPosition } from "./useAnchoredPosition";

// Re-export types for convenience
export type { PopoverSide, PopoverAlign };

type PopoverProps = {
  trigger: ReactNode;
  content?: ReactNode;
  renderContent?: (close: () => void) => ReactNode;
  side?: PopoverSide;
  align?: PopoverAlign;
  className?: string;
  panelClassName?: string;
  stopPropagation?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Accessible name for the focusable trigger wrapper. Use when the trigger's
   * own text content does not fully convey its purpose (e.g. a date picker that
   * relies on a separate visible field label).
   */
  triggerAriaLabel?: string;
  panelAriaLabel?: string;
  /**
   * Extra classes merged onto the focusable trigger wrapper span.
   * Use to stretch the trigger (e.g. "w-full") instead of reaching in
   * from the outside with `[&>span]:` arbitrary variants.
   */
  triggerClassName?: string;
  /**
   * Maximum height of the popover panel (CSS value, e.g. "60vh" or "400px").
   * On mobile/modal mode, this is overridden by viewport constraints.
   */
  maxHeight?: string;
  /** Padding from viewport edges in pixels (default: 12) */
  viewportPadding?: number;
  /** Offset from trigger element in pixels (default: 8) */
  offsetDistance?: number;
  /**
   * Force modal mode regardless of viewport size.
   * By default, modal mode is auto-enabled on mobile viewports.
   */
  forceModal?: boolean;
  /**
   * Disable modal mode on mobile (always use positioned popover).
   * Use with caution - may cause overflow on small screens.
   */
  disableMobileModal?: boolean;
};

// Focus trap selector
const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export const Popover = ({
  trigger,
  content,
  renderContent,
  side = "bottom",
  align = "center",
  className,
  panelClassName,
  stopPropagation = false,
  onOpenChange,
  triggerAriaLabel,
  panelAriaLabel,
  triggerClassName,
  maxHeight,
  viewportPadding = 12,
  offsetDistance = 8,
  forceModal = false,
  disableMobileModal = false,
}: PopoverProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const prevOpenRef = useRef(open);

  // Mobile detection for modal mode
  const isMobileViewport = useIsMobile();
  const isModalMode = forceModal || (isMobileViewport && !disableMobileModal);

  // Position tracking (desktop mode only -- the modal branch is centred by flex)
  const anchored = useAnchoredPosition({
    open: open && !isModalMode,
    anchor: triggerRef,
    panelRef,
    side,
    align,
    viewportPadding,
    offsetDistance,
  });

  // Notify parent of open state changes
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  // Close handler with event isolation
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // Document-level event handlers (click outside, ESC)
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  // Focus management
  useEffect(() => {
    if (!open) return;

    const focusTimer = window.setTimeout(() => {
      if (!panelRef.current) return;

      // Find first focusable element or focus the panel itself
      const focusable = panelRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable) {
        focusable.focus();
      } else {
        panelRef.current.focus();
      }
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [open]);

  // Return focus to trigger when closing
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      triggerRef.current?.focus();
    }
    prevOpenRef.current = open;
  }, [open]);

  // Stop propagation for all clicks inside the popover panel to prevent click-through
  // to underlying elements (e.g., DataTable rows triggering edit modals)
  const handlePanelClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handlePanelMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  // Focus trap for modal mode
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isModalMode || event.key !== "Tab") return;

    const container = panelRef.current;
    if (!container) return;

    const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Handle overlay click for modal mode
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  // Resolved max height
  const resolvedMaxHeight = maxHeight ?? (isModalMode ? "80vh" : "min(70vh, 600px)");

  // Render modal mode (mobile)
  const renderModalContent = () => (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-3"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        id={panelId}
        ref={panelRef}
        role="dialog"
        aria-label={panelAriaLabel}
        aria-modal="true"
        tabIndex={-1}
        style={{
          maxHeight: resolvedMaxHeight,
        }}
        className={cn(
          "w-full max-w-[calc(100vw-24px)] sm:max-w-md",
          "flex flex-col overflow-hidden",
          "rounded-xl border border-border/60 bg-card/95 text-sm text-foreground shadow-xl outline-none",
          "animate-in fade-in-0 zoom-in-95 duration-200",
          panelClassName
        )}
        onClick={handlePanelClick}
        onMouseDown={handlePanelMouseDown}
        onKeyDown={handleKeyDown}
      >
        {renderContent ? renderContent(close) : content}
      </div>
    </div>
  );

  // Render positioned popover (desktop)
  const renderPositionedContent = () => (
    <div
      id={panelId}
      ref={panelRef}
      role="dialog"
      aria-label={panelAriaLabel}
      tabIndex={-1}
      style={{
        ...anchored.style,
        // Clamp the design cap to the space the positioning middleware measured,
        // so a positioned popover never overflows the viewport near an edge.
        maxHeight: `min(${resolvedMaxHeight}, var(--qb-available-h, 100vh))`,
        maxWidth: "var(--qb-available-w, 100vw)",
      }}
      className={cn(
        "z-[9999] flex flex-col overflow-hidden",
        // Fully opaque surface: a positioned popover has no backdrop overlay, so
        // a translucent panel lets whatever sits behind it (e.g. a card's still-
        // rendered hover action cluster) ghost through. `bg-card` is a solid RGB
        // triplet on every theme; the shadow provides the elevation cue.
        "rounded-lg border border-border/60 bg-card text-sm text-foreground shadow-soft outline-none",
        anchored.className,
        panelClassName
      )}
      onClick={handlePanelClick}
      onMouseDown={handlePanelMouseDown}
    >
      {renderContent ? renderContent(close) : content}
    </div>
  );

  return (
    <div className={cn("relative inline-flex", className)}>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        aria-label={triggerAriaLabel}
        onClick={(event) => {
          if (stopPropagation) {
            event.preventDefault();
            event.stopPropagation();
          }
          setOpen((prev) => !prev);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        className={cn(
          "inline-flex cursor-pointer items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
          triggerClassName
        )}
      >
        {trigger}
      </span>
      {open &&
        createPortal(
          isModalMode ? renderModalContent() : renderPositionedContent(),
          document.body
        )}
    </div>
  );
};
